/**
 * "Added by AI" attribution badge for itinerary entities — ADR-0084.
 *
 * Renders a small chip next to a Place / Transport / Custom / Journey /
 * Stay card title when the entity carries `createdBy: 'assistant'` or
 * `lastModifiedBy: 'assistant'` from the assistant write dispatcher
 * (Slice 2 stamps attribution on commit). Two suppression paths:
 *
 *   1. Global — `userPrefs.hideAiBadges === true` hides every badge.
 *   2. Per-entity — `userPrefs.acknowledgedAiAdds` carries entity ids
 *      the user has tapped to dismiss. Tap on the badge writes the
 *      entity id into the list (debounced via the userPrefs.update
 *      mutation).
 *
 * Co-located under `components/assistant/` because the badge is part
 * of the assistant feature, not a generic ui primitive (CLAUDE.md
 * "Adding to libs/ui": one live consumer = local component, not lib).
 */

import { useCallback, useMemo, useState } from 'react';
import { Pressable } from 'react-native';
import { Text, XStack } from 'tamagui';
import type {
  EntityAuthorshipFields,
  UserPreferences,
} from '@my-fullstack-app/itinerary-types';
import { useThemeColors } from '@kiips/ui';
import { trpc } from '../../lib/trpc';

export interface AiBadgeProps {
  /** Entity id used as the dismissal key in
   *  `userPrefs.acknowledgedAiAdds`. */
  entityId: string;
  /** Authorship fields lifted off the entity. Pass the entity directly
   *  rather than spreading — the renderer reads `createdBy` and
   *  `lastModifiedBy` to decide variant text ("Added by AI" vs
   *  "Edited by AI"). */
  entity: EntityAuthorshipFields;
  /** Layout variant. `compact` is the in-card chip (defaults). `inline`
   *  drops the background so the badge fits inside a header chip row. */
  variant?: 'compact' | 'inline';
}

export function AiBadge({ entityId, entity, variant = 'compact' }: AiBadgeProps) {
  const colors = useThemeColors();
  const utils = trpc.useUtils();
  const prefsQuery = trpc.userPrefs.get.useQuery(undefined, {
    staleTime: Infinity,
  });
  // Local optimistic-dismissal state so the tap feels instant; the
  // tRPC mutation invalidates the cached prefs in the background and
  // the server-side state catches up. Avoids the exactOptionalPropertyTypes
  // friction of patching the cached prefs object in place.
  const updateMutation = trpc.userPrefs.update.useMutation({
    onSettled: () => {
      void utils.userPrefs.get.invalidate();
    },
  });
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  const prefs = (prefsQuery.data as UserPreferences | undefined) ?? {};
  const isDismissed = useMemo(
    () => (prefs.acknowledgedAiAdds ?? []).includes(entityId),
    [prefs.acknowledgedAiAdds, entityId],
  );

  const handleDismiss = useCallback(() => {
    const acknowledged = new Set(prefs.acknowledgedAiAdds ?? []);
    if (acknowledged.has(entityId)) return;
    acknowledged.add(entityId);
    setLocallyDismissed(true);
    updateMutation.mutate({
      acknowledgedAiAdds: [...acknowledged],
    });
  }, [entityId, prefs.acknowledgedAiAdds, updateMutation]);

  // Visibility gates: read attribution off the entity, then suppress
  // via the two prefs.
  const isAttributed =
    entity.createdBy === 'assistant' ||
    entity.lastModifiedBy === 'assistant';
  if (!isAttributed) return null;
  if (prefs.hideAiBadges) return null;
  if (isDismissed || locallyDismissed) return null;

  const label = entity.createdBy === 'assistant' ? 'AI' : 'AI';
  const accessibilityLabel =
    entity.createdBy === 'assistant'
      ? 'Added by the assistant. Tap to dismiss.'
      : 'Edited by the assistant. Tap to dismiss.';

  const inline = variant === 'inline';
  return (
    <Pressable
      onPress={handleDismiss}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={{
        paddingHorizontal: inline ? 6 : 8,
        paddingVertical: inline ? 1 : 2,
        borderRadius: 6,
        backgroundColor: inline ? 'transparent' : colors.sectionBgAlt,
        borderWidth: 1,
        borderColor: colors.borderDefault,
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <XStack gap={4} alignItems="center">
        <Text fontSize={10} fontWeight="700" color={colors.textSubdued as never}>
          {label}
        </Text>
      </XStack>
    </Pressable>
  );
}
